import { useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';

/**
 * Hook for field-level locking.
 * Returns handlers to attach to form fields for automatic lock/unlock.
 *
 * Usage:
 *   const { getFieldProps, isLocked, lockInfo } = useFieldLock('comms-plan');
 *   <Input {...getFieldProps('frequency_name')} value={...} onChange={...} />
 */
export const useFieldLock = (formId) => {
  const socket = useSocket();

  const getFieldProps = useCallback((fieldKey) => {
    if (!socket) return {};

    const lock = socket.isFieldLocked(formId, fieldKey);
    const isLocked = !!lock;

    return {
      onFocus: () => socket.lockField(formId, fieldKey),
      onBlur: () => socket.unlockField(formId, fieldKey),
      disabled: isLocked,
      readOnly: isLocked,
      title: isLocked ? `Editing by ${lock.user_name}` : undefined,
      style: isLocked ? {
        borderColor: '#f59e0b',
        boxShadow: '0 0 0 1px #f59e0b',
        opacity: 0.7,
        cursor: 'not-allowed',
      } : undefined,
      'data-locked-by': isLocked ? lock.user_name : undefined,
    };
  }, [socket, formId]);

  const isLocked = useCallback((fieldKey) => {
    if (!socket) return false;
    return !!socket.isFieldLocked(formId, fieldKey);
  }, [socket, formId]);

  const lockInfo = useCallback((fieldKey) => {
    if (!socket) return null;
    return socket.isFieldLocked(formId, fieldKey);
  }, [socket, formId]);

  return { getFieldProps, isLocked, lockInfo };
};
